/**
 * Valnexo CRM - Academy Page
 * ===========================
 * Page principale de la section formation avec:
 * - Liste des modules par catégorie
 * - Progression globale
 * - Accès aux modules
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { academyAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
    GraduationCap, BookOpen, Play, CheckCircle2, Clock, Award,
    Zap, HardHat, Monitor, BarChart3, Users, ChevronRight,
    Plus, Settings, Lock, ShieldCheck, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Icônes par catégorie
const CATEGORY_ICONS = {
    crm_valnexo: Monitor,
    easy_power: Zap,
    chantier: HardHat,
    general: BookOpen
};

const CATEGORY_COLORS = {
    crm_valnexo: 'bg-blue-100 text-blue-700 border-blue-200',
    easy_power: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    chantier: 'bg-orange-100 text-orange-700 border-orange-200',
    general: 'bg-slate-100 text-slate-700 border-slate-200'
};

const CATEGORY_NAMES = {
    crm_valnexo: 'CRM Valnexo',
    easy_power: 'Easy Power',
    chantier: 'Chantier',
    general: 'Général'
};

// Carte de module
const ModuleCard = ({ module, onClick }) => {
    const CategoryIcon = CATEGORY_ICONS[module.category] || BookOpen;
    const progress = module.user_progress || {};
    const isCompleted = progress.completed;
    const hasStarted = progress.progress_percent > 0;
    
    return (
        <Card 
            className={cn(
                "cursor-pointer transition-all hover:shadow-lg hover:border-primary/50",
                isCompleted && "border-emerald-300 bg-emerald-50/30"
            )}
            onClick={onClick}
            data-testid={`module-card-${module.id}`}
        >
            <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                    <Badge 
                        variant="outline" 
                        className={cn("text-xs", CATEGORY_COLORS[module.category])}
                    >
                        <CategoryIcon className="h-3 w-3 mr-1" />
                        {CATEGORY_NAMES[module.category]}
                    </Badge>
                    <div className="flex items-center gap-1.5">
                        {progress.quiz_has_updates && (
                            <Badge 
                                variant="outline" 
                                className="text-[10px] py-0 h-5 border-orange-300 text-orange-700 bg-orange-50 animate-pulse"
                                data-testid="badge-quiz-update"
                            >
                                <Sparkles className="h-3 w-3 mr-0.5" />
                                Mise à jour
                            </Badge>
                        )}
                        {isCompleted && (
                            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                        )}
                        {!module.is_published && (
                            <Badge variant="secondary" className="text-xs">
                                <Lock className="h-3 w-3 mr-1" />
                                Brouillon
                            </Badge>
                        )}
                    </div>
                </div>
                <CardTitle className="text-lg mt-2">{module.title}</CardTitle>
                <CardDescription className="line-clamp-2">
                    {module.description}
                </CardDescription>
            </CardHeader>
            <CardContent className="pb-3">
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {module.estimated_duration_minutes} min
                    </div>
                    <div className="flex items-center gap-1">
                        <BookOpen className="h-4 w-4" />
                        {module.contents_count || 0} contenus
                    </div>
                    {module.has_quiz && (
                        <div className="flex items-center gap-1">
                            <Award className="h-4 w-4" />
                            Quiz
                        </div>
                    )}
                </div>
            </CardContent>
            <CardFooter className="pt-0">
                <div className="w-full space-y-2">
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Progression</span>
                        <span className="font-medium">{progress.progress_percent || 0}%</span>
                    </div>
                    <Progress value={progress.progress_percent || 0} className="h-2" />
                    {progress.quiz_passed && (
                        <div className="flex items-center gap-1 text-xs text-emerald-600">
                            <CheckCircle2 className="h-3 w-3" />
                            Quiz réussi ({progress.quiz_score}%)
                        </div>
                    )}
                </div>
            </CardFooter>
        </Card>
    );
};

// Stats globales utilisateur
const UserStats = ({ stats }) => {
    if (!stats) return null;
    
    return (
        <div className="grid gap-4 md:grid-cols-4 mb-6">
            <Card>
                <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <BookOpen className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{stats.completed_modules}/{stats.total_modules}</p>
                            <p className="text-xs text-muted-foreground">Modules complétés</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{stats.completion_rate}%</p>
                            <p className="text-xs text-muted-foreground">Taux de complétion</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <Clock className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{stats.total_time_spent_formatted}</p>
                            <p className="text-xs text-muted-foreground">Temps de formation</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-yellow-100 rounded-lg">
                            <Award className="h-5 w-5 text-yellow-600" />
                        </div>
                        <div>
                            <Progress value={stats.completion_rate} className="h-2 w-20" />
                            <p className="text-xs text-muted-foreground mt-1">Progression globale</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default function Academy() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [modules, setModules] = useState([]);
    const [myProgress, setMyProgress] = useState(null);
    const [activeCategory, setActiveCategory] = useState('all');
    
    const isAdminFull = (user?.role === 'admin_full' || user?.is_admin === true);
    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;
    const isVP = user?.role === 'vp';

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [modulesRes, progressRes] = await Promise.all([
                academyAPI.getModules(),
                academyAPI.getMyProgress()
            ]);
            setModules(modulesRes.data || []);
            setMyProgress(progressRes.data);
        } catch (err) {
            console.error('Erreur chargement academy:', err);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const filteredModules = activeCategory === 'all' 
        ? modules 
        : modules.filter(m => m.category === activeCategory);

    // Grouper par catégorie pour l'affichage
    const modulesByCategory = modules.reduce((acc, module) => {
        const cat = module.category || 'general';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(module);
        return acc;
    }, {});

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-10 w-64" />
                    <div className="grid gap-4 md:grid-cols-4">
                        {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
                    </div>
                    <div className="grid gap-4 md:grid-cols-3">
                        {[1,2,3].map(i => <Skeleton key={i} className="h-48" />)}
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="academy-page">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <GraduationCap className="h-7 w-7 text-primary" />
                            Académie
                        </h1>
                        <p className="text-muted-foreground">
                            Formez-vous aux outils et processus Valnexo
                        </p>
                    </div>
                    <div className="flex gap-2">
                        {isAdmin && (
                            <Button 
                                variant="outline" 
                                onClick={() => navigate('/academy/admin')}
                                data-testid="admin-dashboard-btn"
                            >
                                <BarChart3 className="h-4 w-4 mr-2" />
                                Dashboard Admin
                            </Button>
                        )}
                        {isVP && (
                            <Button 
                                variant="outline" 
                                onClick={() => navigate('/academy/supervision')}
                                data-testid="supervision-dashboard-btn"
                            >
                                <ShieldCheck className="h-4 w-4 mr-2" />
                                Dashboard Supervision
                            </Button>
                        )}
                        {isAdminFull && (
                            <Button 
                                onClick={() => navigate('/academy/manage')}
                                data-testid="manage-modules-btn"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Gérer les modules
                            </Button>
                        )}
                    </div>
                </div>

                {/* Stats utilisateur */}
                <UserStats stats={myProgress?.stats} />

                {/* Filtres par catégorie */}
                <Tabs value={activeCategory} onValueChange={setActiveCategory}>
                    <TabsList>
                        <TabsTrigger value="all">
                            Tous les modules
                        </TabsTrigger>
                        <TabsTrigger value="crm_valnexo">
                            <Monitor className="h-4 w-4 mr-1" />
                            CRM Valnexo
                        </TabsTrigger>
                        <TabsTrigger value="easy_power">
                            <Zap className="h-4 w-4 mr-1" />
                            Easy Power
                        </TabsTrigger>
                        <TabsTrigger value="chantier">
                            <HardHat className="h-4 w-4 mr-1" />
                            Chantier
                        </TabsTrigger>
                        <TabsTrigger value="general">
                            <BookOpen className="h-4 w-4 mr-1" />
                            Général
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value={activeCategory} className="mt-6">
                        {filteredModules.length === 0 ? (
                            <Card>
                                <CardContent className="py-12 text-center">
                                    <GraduationCap className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                    <p className="text-muted-foreground">
                                        Aucun module disponible dans cette catégorie
                                    </p>
                                    {isAdminFull && (
                                        <Button 
                                            className="mt-4" 
                                            onClick={() => navigate('/academy/manage')}
                                        >
                                            <Plus className="h-4 w-4 mr-2" />
                                            Créer un module
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {filteredModules.map(module => (
                                    <ModuleCard
                                        key={module.id}
                                        module={module}
                                        onClick={() => navigate(`/academy/module/${module.id}`)}
                                    />
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
}
